import { useEffect, useState } from "react";
import { useTheme } from "../../../context/ThemeProvider";
import chatSidebarTime from "../../../utils/chatSidebarTime";
import TextTranslate from "../../../utils/TextTranslate";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import { useConversationLinks } from "../../../store/api/useConversationLinks";
import { ClipLoader } from "react-spinners";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";
import {
  faviconGuessUrl,
  fetchMetadata,
  metadataFallback,
  type LinkMetadata,
} from "../../../components/fetchMetadata";
import {
  displayReadableUrl,
  hostnameFromUrl,
} from "../../../utils/displayReadableUrl";

function LinkRow({
  url,
  timeLabel,
}: {
  url: string;
  timeLabel: string;
}) {
  // @ts-ignore
  const { theme } = useTheme();
  const dark = theme === "dark";
  const [meta, setMeta] = useState<LinkMetadata>(() => metadataFallback(url));
  const [favFailed, setFavFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFavFailed(false);
    setMeta(metadataFallback(url));
    fetchMetadata(url).then((m) => {
      if (!cancelled && m) setMeta(m);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const host = meta.publisher || hostnameFromUrl(url);
  const title = meta.title || displayReadableUrl(url);
  const fav = meta.logo || faviconGuessUrl(url);

  return (
    <a
      href={url.startsWith("http") ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-3 py-3 active:bg-black/[0.04] dark:active:bg-white/[0.06]"
    >
      <div
        className={`grid h-11 w-11 shrink-0 place-content-center overflow-hidden rounded-xl ${
          dark ? "bg-white/10" : "bg-black/[0.06]"
        }`}
      >
        {!favFailed && fav ? (
          <img
            src={fav}
            alt=""
            className="h-6 w-6 object-contain"
            onError={() => setFavFailed(true)}
          />
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="text-lightText"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-medium text-darkText">
          {title}
        </div>
        <div className="truncate text-[12px] text-lightText">{host}</div>
        <div className="mt-0.5 text-[11px] text-lightText/80">{timeLabel}</div>
      </div>
    </a>
  );
}

export default function Links() {
  // @ts-ignore
  const { theme } = useTheme();
  const { translate, hour12 } = useTimeFormatOptions();
  const { data, isLoading } = useConversationLinks();
  const records = data?.data?.Records || [];

  if (isLoading) {
    return (
      <div className="grid min-h-[50vh] place-content-center">
        <ClipLoader color={theme == "dark" ? "#fff" : "#000"} size={22} />
      </div>
    );
  }

  if (!records.length) {
    return (
      <div className="grid min-h-[50vh] place-content-center gap-4 text-center">
        <SearchNotFoundAnimation />
        <div className="text-[15px] text-lightText">
          <TextTranslate text="No Links Found" />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      {records.map((message, index) => (
        <div
          key={message.message_id || index}
          className={
            index > 0
              ? "border-t border-black/[0.06] dark:border-white/[0.08]"
              : ""
          }
        >
          <LinkRow
            url={message.message_content}
            timeLabel={chatSidebarTime(message.createdAt, {
              translate,
              hour12,
            })}
          />
        </div>
      ))}
    </div>
  );
}
